import { useState, useEffect, useCallback } from 'react';
import { BoardAPI, ProtectedBoardAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

/* ── color palette ───────────────────────────────────────────────────── */

export const CARD_COLORS = [
  { label: 'Grön',   value: '#2c4a2e' },
  { label: 'Blå',    value: '#3d6b7a' },
  { label: 'Guld',   value: '#8b6914' },
  { label: 'Lila',   value: '#6b3d7a' },
  { label: 'Röd',    value: '#9a2d2d' },
  { label: 'Grå',    value: '#5a6068' },
];

export const DEFAULT_COLOR = CARD_COLORS[0].value;

/* ── toast ───────────────────────────────────────────────────────────── */

function Toast({ message, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [onClose]);
  return (
    <div style={{
      position: 'fixed', bottom: '1.5rem', right: '1.5rem', zIndex: 1000,
      background: type === 'error' ? 'var(--danger)' : 'var(--forest)',
      color: '#fff', padding: '0.85rem 1.25rem', borderRadius: 6,
      boxShadow: '0 4px 20px rgba(0,0,0,.25)',
      display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.88rem'
    }}>
      <span>{type === 'error' ? '✕' : '✓'}</span>
      <span>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,.7)', cursor: 'pointer', fontSize: '1rem', padding: 0 }}>×</button>
    </div>
  );
}

/* ── member drawer ───────────────────────────────────────────────────── */

const EMPTY_MEMBER = { roll: '', namn: '', telefon: '', epost: '', adress: '', beskrivning: '', color: DEFAULT_COLOR };

function MemberDrawer({ open, onClose, onSave, member }) {
  const [form, setForm] = useState(EMPTY_MEMBER);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm(member ? {
      roll:        member.roll        || '',
      namn:        member.namn        || '',
      telefon:     member.telefon     || '',
      epost:       member.epost       || '',
      adress:      member.adress      || '',
      beskrivning: member.beskrivning || '',
      color:       member.color       || DEFAULT_COLOR,
    } : { ...EMPTY_MEMBER });
    setError('');
  }, [open, member]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.roll.trim()) { setError('Roll är obligatorisk'); return; }
    if (!form.namn.trim()) { setError('Namn är obligatoriskt'); return; }
    setSaving(true);
    try {
      await onSave({ ...form, roll: form.roll.trim(), namn: form.namn.trim() });
      onClose();
    } catch (err) {
      setError(err.message || 'Något gick fel');
    } finally {
      setSaving(false);
    }
  };

  const label = {
    display: 'block', fontSize: '0.8rem', fontWeight: 600,
    letterSpacing: '0.05em', textTransform: 'uppercase',
    color: 'var(--text-soft)', marginBottom: '0.4rem'
  };
  const input = {
    width: '100%', padding: '0.6rem 0.85rem',
    border: '1px solid var(--border)', borderRadius: 4,
    fontSize: '0.9rem', background: 'var(--white)', color: 'var(--text)',
    boxSizing: 'border-box'
  };

  return (
    <>
      {open && <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 200 }} />}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 440, maxWidth: '95vw',
        background: 'var(--white)', zIndex: 201, overflowY: 'auto',
        boxShadow: '-8px 0 40px rgba(0,0,0,.18)',
        transform: open ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform .28s cubic-bezier(.4,0,.2,1)',
        display: 'flex', flexDirection: 'column'
      }}>
        {/* preview strip */}
        <div style={{ height: 6, background: form.color, transition: 'background .2s' }} />

        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ fontFamily: '"Playfair Display", serif', fontSize: '1.2rem', color: 'var(--forest)', margin: 0 }}>
            {member ? 'Redigera ledamot' : 'Lägg till ledamot'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.4rem', color: 'var(--text-soft)', padding: '0.2rem 0.4rem' }}>×</button>
        </div>

        <form onSubmit={handleSubmit} style={{ flex: 1, padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
          {error && (
            <div style={{ padding: '0.75rem 1rem', background: 'rgba(176,48,48,.08)', border: '1px solid rgba(176,48,48,.3)', borderRadius: 4, fontSize: '0.88rem', color: 'var(--danger)' }}>
              {error}
            </div>
          )}

          <div>
            <label style={label}>Roll / titel</label>
            <input style={input} value={form.roll} onChange={e => set('roll', e.target.value)} placeholder="T.ex. Ordförande, Kassör…" />
          </div>

          <div>
            <label style={label}>Namn</label>
            <input style={input} value={form.namn} onChange={e => set('namn', e.target.value)} placeholder="Förnamn Efternamn" />
          </div>

          <div>
            <label style={label}>Beskrivning / ansvarsområde</label>
            <input
              style={input}
              value={form.beskrivning}
              onChange={e => set('beskrivning', e.target.value)}
              placeholder="T.ex. Ansvarar för ekonomi och avgifter"
            />
          </div>

          <div>
            <label style={label}>Adress</label>
            <input
              style={input}
              value={form.adress}
              onChange={e => set('adress', e.target.value)}
              placeholder="T.ex. Hannäsvägen 164, 642 94 Flen"
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div>
              <label style={label}>Telefon</label>
              <input style={input} type="tel" value={form.telefon} onChange={e => set('telefon', e.target.value)} placeholder="070-000 00 00" />
            </div>
            <div>
              <label style={label}>E-post</label>
              <input style={input} type="email" value={form.epost} onChange={e => set('epost', e.target.value)} placeholder="namn@example.com" />
            </div>
          </div>

          <div>
            <label style={label}>Kortfärg</label>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {CARD_COLORS.map(c => (
                <button
                  key={c.value}
                  type="button"
                  title={c.label}
                  onClick={() => set('color', c.value)}
                  style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: c.value, border: 'none', cursor: 'pointer',
                    outline: form.color === c.value ? `3px solid ${c.value}` : '3px solid transparent',
                    outlineOffset: 2,
                    transition: 'outline .15s'
                  }}
                />
              ))}
            </div>
          </div>

          <div style={{ marginTop: 'auto', display: 'flex', gap: '0.75rem', paddingTop: '1rem', borderTop: '1px solid var(--border)' }}>
            <button type="button" onClick={onClose} disabled={saving} className="btn btn-ghost" style={{ flex: 1 }}>Avbryt</button>
            <button type="submit" disabled={saving} className="btn btn-forest" style={{ flex: 2 }}>
              {saving ? 'Sparar...' : member ? 'Spara ändringar' : 'Lägg till'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

/* ── member card ─────────────────────────────────────────────────────── */

function MemberCard({ member, index, total, onEdit, onMove, onDelete, canDelete, canEdit }) {
  const [hov, setHov] = useState(false);
  const isVacant = !member.namn || member.namn.toLowerCase().startsWith('vakant');
  const accent = member.color || DEFAULT_COLOR;

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: '1rem',
        padding: '0.9rem 1.1rem', borderRadius: 6,
        border: `1px solid var(--border)`,
        borderLeft: `4px solid ${isVacant ? 'var(--border)' : accent}`,
        background: hov ? 'rgba(0,0,0,.02)' : 'var(--white)',
        transition: 'background .15s'
      }}
    >
      {/* reorder buttons */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem', flexShrink: 0 }}>
        <button
          onClick={() => canEdit && onMove(index, -1)}
          disabled={!canEdit || index === 0}
          title={canEdit ? '' : 'Kräver administratörsbehörighet'}
          style={{
            background: 'none', border: 'none', cursor: canEdit && index !== 0 ? 'pointer' : 'not-allowed',
            color: 'var(--text-soft)', fontSize: '0.7rem', padding: '0.1rem 0.25rem',
            opacity: !canEdit || index === 0 ? 0.25 : 0.6, lineHeight: 1
          }}>▲</button>
        <button
          onClick={() => canEdit && onMove(index, 1)}
          disabled={!canEdit || index === total - 1}
          title={canEdit ? '' : 'Kräver administratörsbehörighet'}
          style={{
            background: 'none', border: 'none', cursor: canEdit && index !== total - 1 ? 'pointer' : 'not-allowed',
            color: 'var(--text-soft)', fontSize: '0.7rem', padding: '0.1rem 0.25rem',
            opacity: !canEdit || index === total - 1 ? 0.25 : 0.6, lineHeight: 1
          }}>▼</button>
      </div>

      {/* avatar */}
      <div style={{
        width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
        background: isVacant ? 'var(--border)' : accent,
        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '0.78rem', fontWeight: 700
      }}>
        {isVacant ? '?' : member.namn.split(' ').map(w => w[0]).slice(0, 2).join('')}
      </div>

      {/* info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: accent, marginBottom: '0.1rem' }}>
          {member.roll}
        </div>
        <div style={{ fontSize: '0.9rem', fontWeight: 500, color: isVacant ? 'var(--text-soft)' : 'var(--text)', fontStyle: isVacant ? 'italic' : 'normal' }}>
          {member.namn || '—'}
        </div>
        {member.beskrivning && (
          <div style={{ fontSize: '0.75rem', color: 'var(--text-soft)', marginTop: '0.1rem' }}>
            {member.beskrivning}
          </div>
        )}
        <div style={{ fontSize: '0.72rem', color: 'var(--text-soft)', marginTop: '0.2rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {member.adress && <span>📍 {member.adress}</span>}
          {member.telefon && <span>📞 {member.telefon}</span>}
          {member.epost && <span>✉ {member.epost}</span>}
        </div>
      </div>

      {/* actions */}
      <div style={{ display: 'flex', gap: '0.2rem', flexShrink: 0, opacity: hov ? 1 : 0.3, transition: 'opacity .15s' }}>
        <button
          onClick={() => canEdit && onEdit(member)}
          disabled={!canEdit}
          title={canEdit ? 'Redigera' : 'Kräver administratörsbehörighet'}
          style={{
            background: 'none', border: 'none', cursor: canEdit ? 'pointer' : 'not-allowed',
            padding: '0.4rem 0.5rem', borderRadius: 3, color: canEdit ? 'var(--text-soft)' : '#9CA3AF', fontSize: '0.85rem',
            opacity: canEdit ? 1 : 0.5
          }}
        >✏</button>
        <button
          onClick={() => canEdit && onDelete(index)}
          disabled={!canEdit}
          title={canEdit ? 'Ta bort' : 'Kräver administratörsbehörighet'}
          style={{
            background: 'none', border: 'none', cursor: canEdit ? 'pointer' : 'not-allowed',
            padding: '0.4rem 0.5rem', borderRadius: 3, color: canEdit ? 'var(--danger)' : '#9CA3AF', fontSize: '0.85rem',
            opacity: canEdit ? 1 : 0.5
          }}
        >🗑</button>
      </div>
    </div>
  );
}

/* ── main component ──────────────────────────────────────────────────── */

export default function BoardManager() {
  const { user, hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  const canEdit = hasRole('contentCreator');
  const [board, setBoard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [editIndex, setEditIndex] = useState(null);
  const [toast, setToast] = useState(null);

  const notify = (message, type = 'success') => setToast({ message, type });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await BoardAPI.getAll();
      setBoard(d.board || []);
    } catch { notify('Kunde inte ladda styrelsedata', 'error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveBoard = async (updatedBoard) => {
    setSaving(true);
    try {
      await ProtectedBoardAPI.update({ board: updatedBoard });
      await load();
      notify('Styrelsen sparad');
    } catch { notify('Kunde inte spara', 'error'); }
    finally { setSaving(false); }
  };

  const openAdd = () => { setEditMember(null); setEditIndex(null); setShowDrawer(true); };

  const openEdit = (member) => {
    setEditMember(member);
    setEditIndex(board.findIndex(m => m === member));
    setShowDrawer(true);
  };

  const handleSave = async (data) => {
    let updated;
    if (editIndex !== null && editIndex >= 0) {
      updated = board.map((m, i) => i === editIndex ? { ...m, ...data } : m);
    } else {
      updated = [...board, { id: `tmp-${Date.now()}`, ...data }];
    }
    await saveBoard(updated);
  };

  const handleMove = async (index, dir) => {
    const swap = index + dir;
    if (swap < 0 || swap >= board.length) return;
    const updated = [...board];
    [updated[index], updated[swap]] = [updated[swap], updated[index]];
    await saveBoard(updated);
  };

  const handleDelete = async (index) => {
    if (!confirm(`Ta bort ${board[index].namn || board[index].roll}?`)) return;
    const updated = board.filter((_, i) => i !== index);
    await saveBoard(updated);
  };

  return (
    <div style={{ maxWidth: 700, margin: '0 auto', padding: '2rem 1.5rem' }}>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.75rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h2 style={{ fontFamily: '"Playfair Display", serif', fontSize: '1.6rem', color: 'var(--forest)', margin: 0 }}>Styrelse</h2>
          <p style={{ fontSize: '0.82rem', color: 'var(--text-soft)', marginTop: '0.2rem' }}>
            {board.length} ledamöter · presenteras på kontaktsidan
          </p>
        </div>
        {isAdmin && (
          <button onClick={openAdd} disabled={saving} className="btn btn-forest" style={{ fontSize: '0.78rem' }}>
            + Lägg till ledamot
          </button>
        )}
      </div>

      {loading ? (
        <div className="loading">Laddar styrelsedata...</div>
      ) : board.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem 2rem', color: 'var(--text-soft)' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem', opacity: 0.4 }}>👥</div>
          <div style={{ fontSize: '0.95rem', color: 'var(--text-mid)', marginBottom: '0.5rem' }}>Inga styrelseledamöter</div>
          {isAdmin && (
            <button onClick={openAdd} className="btn btn-forest" style={{ marginTop: '1rem', fontSize: '0.78rem' }}>
              + Lägg till första ledamoten
            </button>
          )}
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {board.map((member, i) => (
              <MemberCard
                key={member.id || i}
                member={member}
                index={i}
                total={board.length}
                onEdit={openEdit}
                onMove={handleMove}
                onDelete={handleDelete}
                canDelete={isAdmin}
                canEdit={isAdmin}
              />
            ))}
          </div>
          <div style={{ marginTop: '1.5rem', padding: '0.9rem 1rem', background: 'var(--cream)', border: '1px solid var(--border)', borderRadius: 5, fontSize: '0.78rem', color: 'var(--text-soft)' }}>
            Ordning och uppgifter uppdateras direkt på kontaktsidan när du sparar ändringar.
          </div>
        </>
      )}

      <MemberDrawer
        open={showDrawer && isAdmin}
        onClose={() => setShowDrawer(false)}
        onSave={handleSave}
        member={editMember}
      />
    </div>
  );
}
